// ─────────────────────────────────────────────────────────────────────────────
// THE SHOW'S CANDIDATE SET.
//
// A static, versioned JSON document served from the show's own origin. It is
// the SAME set for every visitor — anonymous or signed in. That sameness is the
// point: the show cannot tailor what it publishes, because it does not know who
// is asking. Personalization happens later and elsewhere (inside the fan's DO).
//
// The card shape matches what `normalizeCardForRanking` in the runtime consumes:
//   {id?, title, summary, topics[], publishedAt, ref:{atUri?, url?}, ...}
// A card's ranking id is `ref.atUri` ?? `ref.url` ?? `id` ?? `card:<index>`.
// We key the local card map by the SAME derivation so the returned order joins
// back cleanly — see `cardRankingId`.
// ─────────────────────────────────────────────────────────────────────────────

import {CANDIDATE_SET_URL} from './config'
import {assertBareShowFetch} from './showFence'

export interface CardRef {
  atUri?: string
  url?: string
}

export interface Card {
  id?: string
  kind?: string
  title: string
  summary?: string
  topics?: string[]
  /**
   * Guest slugs (guest spaces v0): which platformed guests appear in this
   * card. Stamped by scripts/augment-candidate-set.mjs from data/guests.json.
   */
  people?: string[]
  /**
   * The progressive-unlock hook (exchange-candidate-schema.md): non-public
   * cards still LIST (title+summary — the door) so the concierge can explain
   * membership, but their body/ref resolve only server-side for members.
   * Absent = 'public'.
   */
  availability?: 'public' | 'member' | 'transaction'
  publishedAt?: string
  durationLabel?: string
  imageUrl?: string
  ref?: CardRef
  /**
   * An optional PUBLISHER-AUTHORED note ("a hosts' pick", "trending with new
   * listeners") shown in the why-box on the ANONYMOUS shelf, where the mock
   * puts one. It is part of the published set, so it is identical for every
   * visitor and involves no profile — which is exactly why it may be shown
   * without a ranking behind it. It is never shown on the profiled shelf,
   * where the runtime's why-string is the only permitted explanation.
   *
   * The generator does not emit these (the episode index has no editorial
   * copy), so today the anonymous why-box renders the blind line alone.
   */
  editorialNote?: string
}

export interface CandidateSet {
  /** Monotonic set version — the only thing the publisher observes varying. */
  version: number
  /** The publisher DID (author-weight target for the scorer). */
  publisher: string | null
  /** The show's members group (at:// URI) — the door member cards resolve against. */
  membershipGroup?: string | null
  cards: Card[]
}

/**
 * Derive a card's ranking id EXACTLY as the runtime does
 * (`normalizeCardForRanking`), so `order[].id` joins back to a card.
 */
export function cardRankingId(card: Card, index: number): string {
  const atUri = card?.ref?.atUri
  if (typeof atUri === 'string' && atUri) return atUri
  const url = card?.ref?.url
  if (typeof url === 'string' && url) return url
  if (typeof card.id === 'string' && card.id) return card.id
  return `card:${index}`
}

/**
 * Fetch the candidate set from the show's origin.
 *
 * A BARE GET — no query string, no headers, no credentials, no body. This is
 * the entire show-side request path for a signed-in fan, and `assertBareShowFetch`
 * enforces that it stays that way.
 */
export async function loadCandidateSet(url: string = CANDIDATE_SET_URL): Promise<CandidateSet> {
  const init: RequestInit = {method: 'GET', credentials: 'omit'}
  assertBareShowFetch(url, init)

  const res = await fetch(url, init)
  if (!res.ok) throw new Error(`candidate set unavailable (${res.status})`)
  const doc = await res.json()

  return {
    version: Number.isFinite(Number(doc?.version)) ? Number(doc.version) : 0,
    publisher: typeof doc?.publisher === 'string' ? doc.publisher : null,
    membershipGroup: typeof doc?.membershipGroup === 'string' ? doc.membershipGroup : null,
    cards: Array.isArray(doc?.cards) ? doc.cards : [],
  }
}

/** Availability with the public default (absent = public). */
export function cardAvailability(card: Card): 'public' | 'member' | 'transaction' {
  return card.availability === 'member' || card.availability === 'transaction' ? card.availability : 'public'
}

/** Reverse-chronological order — the anonymous shelf (rungs 0–1). */
export function reverseChronological(cards: Card[]): Card[] {
  return [...cards].sort((a, b) => String(b.publishedAt ?? '').localeCompare(String(a.publishedAt ?? '')))
}
